import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import type { FieldEngineerRecommendation } from '@/types/ticket'
import { normalizeFeDispatchRole } from '@/components/forms/parentTicket/feDispatchRole'

type ResolveAssignmentsInput = {
  primary: FEDispatchSelection | null
  /** First additional FE (legacy single-slot revalidation path). */
  additional?: FEDispatchSelection | null
  /** @deprecated Prefer `additional` */
  backup?: FEDispatchSelection | null
  recommendations: FieldEngineerRecommendation[]
  scheduleIso?: string | null
  unavailableFeUserIds?: Set<number>
  /**
   * @deprecated No longer upgrades still-eligible engineers on location change.
   * Kept for call-site compatibility; ignored.
   */
  reselectBestFit?: boolean
}

export type ResolveAllAssignmentsInput = {
  primary: FEDispatchSelection | null
  additionalFes: FEDispatchSelection[]
  recommendations: FieldEngineerRecommendation[]
  scheduleIso?: string | null
  unavailableFeUserIds?: Set<number>
  /** Authoritative eligibility keyed by FE user id from evaluate-engineer-eligibility. */
  authoritativeByFeUserId?: Record<number, AuthoritativeEligibilityEntry>
  /** When true, eligibility API failed — mark assigned engineers as unknown rather than invalid. */
  eligibilityUnknown?: boolean
}

export type AssignmentEligibility = {
  eligible: boolean
  reasons: string[]
  suggestedReplacement: FieldEngineerRecommendation | null
  /** True when the authoritative eligibility API failed; do not treat as definitive ineligibility. */
  unknown?: boolean
}

export type AuthoritativeEligibilityEntry = {
  fe_user_id: number
  eligible: boolean
  reasons: string[]
  reason_codes?: string[]
}

/** Stable key for identifying an individual assignment across revalidation cycles. */
export type InvalidAssignmentKey = string

export function buildInvalidAssignmentKey(
  role: 'primary' | 'additional',
  feUserId: number,
  slotOrScheduleId?: number | null,
): InvalidAssignmentKey {
  if (role === 'primary') return `primary:${feUserId}`
  return `additional:${slotOrScheduleId ?? 0}:${feUserId}`
}

export type AssignmentEligibilityMap = Map<InvalidAssignmentKey, AssignmentEligibility>

export type ResolveAllAssignmentsResult = {
  primaryEligibility: AssignmentEligibility | null
  additionalEligibilities: AssignmentEligibility[]
  eligibilityMap: AssignmentEligibilityMap
  hasUnresolvedInvalidAssignment: boolean
}

export type ResolveAssignmentsResult = {
  nextPrimary: FEDispatchSelection | null
  nextAdditional: FEDispatchSelection | null
  /** @deprecated Prefer nextAdditional */
  nextBackup?: FEDispatchSelection | null
  /** True only when an explicit reassignment path replaces the engineer (not from revalidation). */
  primaryReplaced: boolean
  additionalCleared: boolean
  /** @deprecated Prefer additionalCleared */
  backupCleared?: boolean
  primaryEligibility: AssignmentEligibility | null
  additionalEligibility: AssignmentEligibility | null
  hasUnresolvedInvalidAssignment: boolean
}

function recommendationByFeUserId(
  recommendations: FieldEngineerRecommendation[],
  feUserId?: number | null,
): FieldEngineerRecommendation | null {
  if (feUserId == null) return null
  return recommendations.find((rec) => Number(rec.user_id) === Number(feUserId)) ?? null
}

export function isEngineerFit(
  rec: FieldEngineerRecommendation | null,
  scheduleIso?: string | null,
  unavailableFeUserIds?: Set<number>,
): boolean {
  if (!rec) return false
  if (rec.has_required_certifications === false) return false
  if (rec.is_locked) return false

  const recUserId = rec.user_id != null ? Number(rec.user_id) : null
  if (recUserId != null && unavailableFeUserIds?.has(recUserId)) return false

  if (scheduleIso && rec.available_at_schedule === false) return false
  return true
}

export function pickTopFitRecommendation(
  recommendations: FieldEngineerRecommendation[],
  scheduleIso?: string | null,
  unavailableFeUserIds?: Set<number>,
  excludeFeUserIds?: Iterable<number | null | undefined>,
): FieldEngineerRecommendation | null {
  const excluded = new Set(
    [...(excludeFeUserIds ?? [])]
      .filter((id): id is number => id != null && Number.isFinite(Number(id)))
      .map(Number),
  )
  return (
    recommendations.find((rec) => {
      if (rec.user_id != null && excluded.has(Number(rec.user_id))) return false
      return isEngineerFit(rec, scheduleIso, unavailableFeUserIds)
    }) ?? null
  )
}

/** Evaluate whether a persisted engineer still satisfies assignment criteria. Does not mutate selection. */
export function evaluateAssignmentEligibility(
  feUserId: number | null | undefined,
  recommendations: FieldEngineerRecommendation[],
  scheduleIso?: string | null,
  unavailableFeUserIds?: Set<number>,
  excludeFeUserIds?: Iterable<number | null | undefined>,
  authoritative?: AuthoritativeEligibilityEntry | null,
  eligibilityUnknown?: boolean,
): AssignmentEligibility {
  if (feUserId == null || !Number.isFinite(Number(feUserId))) {
    return {
      eligible: false,
      reasons: ['No engineer assigned'],
      suggestedReplacement: pickTopFitRecommendation(
        recommendations,
        scheduleIso,
        unavailableFeUserIds,
        excludeFeUserIds,
      ),
    }
  }

  const suggestedReplacement = pickTopFitRecommendation(
    recommendations,
    scheduleIso,
    unavailableFeUserIds,
    excludeFeUserIds,
  )

  if (eligibilityUnknown) {
    return {
      eligible: true,
      unknown: true,
      reasons: ['Eligibility check unavailable — retry before dispatching changes'],
      suggestedReplacement: null,
    }
  }

  if (authoritative) {
    const reasons = authoritative.reasons.length
      ? authoritative.reasons
      : (authoritative.reason_codes ?? []).map((code) => code.replace(/_/g, ' '))
    return {
      eligible: Boolean(authoritative.eligible),
      reasons: authoritative.eligible ? [] : reasons,
      suggestedReplacement: authoritative.eligible ? null : suggestedReplacement,
    }
  }

  const rec = recommendationByFeUserId(recommendations, feUserId)
  const reasons: string[] = []

  if (!rec) {
    // Absence from truncated recommendations is ranking info only, not proof of ineligibility.
    reasons.push('Not in current recommendations (may be ranked lower — verify eligibility)')
  } else {
    if (rec.has_required_certifications === false) {
      reasons.push('Missing required certification')
    }
    if (rec.is_locked) {
      reasons.push('Engineer is locked')
    }
    if (scheduleIso && rec.available_at_schedule === false) {
      reasons.push('Unavailable at the selected schedule')
    }
  }

  const hasScheduleConflict = unavailableFeUserIds?.has(Number(feUserId)) === true
  if (hasScheduleConflict) {
    reasons.push('Schedule conflict with existing assignment')
  }

  // Only hard disqualifiers block when authoritative API is unavailable.
  const eligible = rec != null
    ? isEngineerFit(rec, scheduleIso, unavailableFeUserIds)
    : !hasScheduleConflict

  return {
    eligible,
    reasons,
    suggestedReplacement: eligible ? null : suggestedReplacement,
  }
}

/** Prefill reassignment reason from eligibility failure reasons. */
export function buildReassignmentReasonFromEligibility(reasons: string[]): string {
  if (reasons.some((r) => /certification/i.test(r))) {
    return 'Requirements changed: certification mismatch'
  }
  if (reasons.some((r) => /unavailable|conflict/i.test(r))) {
    return 'Requirements changed: schedule availability'
  }
  if (reasons.some((r) => /recommendations|location|distance/i.test(r))) {
    return 'Requirements changed: location or coverage'
  }
  return 'Requirements changed'
}

function selectionFromRecommendation(
  base: FEDispatchSelection,
  rec: FieldEngineerRecommendation,
  role: 'primary' | 'additional',
): FEDispatchSelection {
  return {
    ...base,
    feUserId: Number(rec.user_id),
    feName: rec.full_name,
    role,
    additionalSlot: role === 'additional' ? (base.additionalSlot ?? 1) : undefined,
  }
}

/**
 * Revalidate persisted assignments after context change without auto-replacing engineers.
 * Still-eligible engineers remain selected even when another recommendation ranks higher.
 */
export function resolveAssignmentsAfterContextChange({
  primary,
  additional,
  backup,
  recommendations,
  scheduleIso,
  unavailableFeUserIds,
}: ResolveAssignmentsInput): ResolveAssignmentsResult {
  const currentAdditional = additional ?? backup ?? null

  const primaryEligibility = primary
    ? evaluateAssignmentEligibility(
        primary.feUserId,
        recommendations,
        scheduleIso,
        unavailableFeUserIds,
        currentAdditional ? [currentAdditional.feUserId] : [],
      )
    : null

  const additionalEligibility = currentAdditional
    ? evaluateAssignmentEligibility(
        currentAdditional.feUserId,
        recommendations,
        scheduleIso,
        unavailableFeUserIds,
        primary ? [primary.feUserId] : [],
      )
    : null

  const hasUnresolvedInvalidAssignment =
    primaryEligibility?.eligible === false || additionalEligibility?.eligible === false

  return {
    nextPrimary: primary,
    nextAdditional: currentAdditional,
    nextBackup: currentAdditional,
    primaryReplaced: false,
    additionalCleared: false,
    backupCleared: false,
    primaryEligibility,
    additionalEligibility,
    hasUnresolvedInvalidAssignment,
  }
}

/**
 * Evaluate ALL assigned engineers (primary + every additional) independently.
 * Returns a Map keyed by stable identity so the caller can track, resolve, and
 * derive `hasUnresolvedInvalidAssignment` from the complete collection.
 */
export function resolveAllAssignmentsAfterContextChange({
  primary,
  additionalFes,
  recommendations,
  scheduleIso,
  unavailableFeUserIds,
  authoritativeByFeUserId,
  eligibilityUnknown,
}: ResolveAllAssignmentsInput): ResolveAllAssignmentsResult {
  const eligibilityMap: AssignmentEligibilityMap = new Map()
  const allAssignedIds = [
    ...(primary ? [primary.feUserId] : []),
    ...additionalFes.map((a) => a.feUserId),
  ]

  let primaryEligibility: AssignmentEligibility | null = null
  if (primary) {
    const excludeIds = additionalFes.map((a) => a.feUserId)
    primaryEligibility = evaluateAssignmentEligibility(
      primary.feUserId,
      recommendations,
      scheduleIso,
      unavailableFeUserIds,
      excludeIds,
      authoritativeByFeUserId?.[primary.feUserId] ?? null,
      eligibilityUnknown,
    )
    const key = buildInvalidAssignmentKey('primary', primary.feUserId)
    eligibilityMap.set(key, primaryEligibility)
  }

  const additionalEligibilities: AssignmentEligibility[] = []
  for (const fe of additionalFes) {
    const excludeIds = allAssignedIds.filter((id) => id !== fe.feUserId)
    const elig = evaluateAssignmentEligibility(
      fe.feUserId,
      recommendations,
      scheduleIso,
      unavailableFeUserIds,
      excludeIds,
      authoritativeByFeUserId?.[fe.feUserId] ?? null,
      eligibilityUnknown,
    )
    additionalEligibilities.push(elig)
    const key = buildInvalidAssignmentKey(
      'additional',
      fe.feUserId,
      fe.additionalSlot,
    )
    eligibilityMap.set(key, elig)
  }

  let hasUnresolvedInvalidAssignment = false
  for (const [, elig] of eligibilityMap) {
    if (!elig.eligible || elig.unknown) {
      hasUnresolvedInvalidAssignment = true
      break
    }
  }

  return {
    primaryEligibility,
    additionalEligibilities,
    eligibilityMap,
    hasUnresolvedInvalidAssignment,
  }
}

/** @deprecated Use evaluateAssignmentEligibility for explicit replacement flows only. */
export function resolveAssignmentsWithReplacement({
  primary,
  additional,
  backup,
  recommendations,
  scheduleIso,
  unavailableFeUserIds,
}: ResolveAssignmentsInput): ResolveAssignmentsResult {
  const currentAdditional = additional ?? backup ?? null
  const primaryRec = recommendationByFeUserId(recommendations, primary?.feUserId ?? null)
  const additionalRec = recommendationByFeUserId(recommendations, currentAdditional?.feUserId ?? null)

  const primaryStillFit = isEngineerFit(primaryRec, scheduleIso, unavailableFeUserIds)
  const additionalStillFit = isEngineerFit(additionalRec, scheduleIso, unavailableFeUserIds)

  let nextPrimary = primary
  let nextAdditional = currentAdditional
  let primaryReplaced = false
  let additionalCleared = false

  if (!primaryStillFit) {
    const replacement = pickTopFitRecommendation(recommendations, scheduleIso, unavailableFeUserIds)
    if (replacement && primary) {
      nextPrimary = selectionFromRecommendation(primary, replacement, 'primary')
      primaryReplaced = true
    } else {
      nextPrimary = null
      primaryReplaced = primary != null
    }
  }

  if (primaryReplaced) {
    if (currentAdditional != null) {
      nextAdditional = null
      additionalCleared = true
    }
  } else if (nextAdditional && !additionalStillFit) {
    nextAdditional = null
    additionalCleared = true
  }

  const primaryEligibility = nextPrimary
    ? evaluateAssignmentEligibility(
        nextPrimary.feUserId,
        recommendations,
        scheduleIso,
        unavailableFeUserIds,
        nextAdditional ? [nextAdditional.feUserId] : [],
      )
    : null
  const additionalEligibility = nextAdditional
    ? evaluateAssignmentEligibility(
        nextAdditional.feUserId,
        recommendations,
        scheduleIso,
        unavailableFeUserIds,
        nextPrimary ? [nextPrimary.feUserId] : [],
      )
    : null

  return {
    nextPrimary,
    nextAdditional,
    nextBackup: nextAdditional,
    primaryReplaced,
    additionalCleared,
    backupCleared: additionalCleared,
    primaryEligibility,
    additionalEligibility,
    hasUnresolvedInvalidAssignment:
      primaryEligibility?.eligible === false || additionalEligibility?.eligible === false,
  }
}

export { normalizeFeDispatchRole }
